import { useMemo, useState } from 'react';
import { Check, CaretUp, CaretDown, DotsSixVertical, X } from '@phosphor-icons/react';
import { Field, SearchInput } from './inputs';

/**
 * Selection primitives — cards, tags, scales and ranking.
 *
 * These replace the dropdowns and radio lists of the original admission form.
 * Everything is a real <button> so keyboard and screen-reader users get the
 * same behaviour as pointer users.
 */

const Tick = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 6.2L4.8 9L10 3.4" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Single choice. */
export function ChoiceCards({ label, optional, hint, note, options, value, onChange, columns = 1 }) {
  return (
    <Field label={label} optional={optional} hint={hint} note={note}>
      <div className="ob-choices" data-cols={columns} role="radiogroup">
        {options.map((option) => {
          const selected = value === option.key;
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={selected}
              className="ob-choice"
              data-selected={selected}
              onClick={() => onChange(option.key)}
            >
              <span className="ob-choice-tick" aria-hidden="true">{selected && <Tick />}</span>
              <span className="ob-choice-text">
                <span className="ob-choice-title">{option.label}</span>
                {option.hint && <span className="ob-choice-hint">{option.hint}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </Field>
  );
}

/** Multiple choice with an optional ceiling. */
export function MultiChoiceCards({
  label, optional, hint, note, options, values = [], onChange, max, columns = 2,
}) {
  const atLimit = max ? values.length >= max : false;

  const toggle = (key) => {
    if (values.includes(key)) {
      onChange(values.filter((v) => v !== key));
    } else if (!atLimit) {
      onChange([...values, key]);
    }
  };

  return (
    <Field label={label} optional={optional} hint={hint} note={note}>
      <div className="ob-choices" data-cols={columns}>
        {options.map((option) => {
          const selected = values.includes(option.key);
          const blocked = atLimit && !selected;
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={selected}
              className="ob-choice"
              data-selected={selected}
              style={blocked ? { opacity: .45 } : undefined}
              onClick={() => toggle(option.key)}
            >
              <span className="ob-choice-tick" data-shape="square" aria-hidden="true">
                {selected && <Tick />}
              </span>
              <span className="ob-choice-text">
                <span className="ob-choice-title">{option.label}</span>
                {option.hint && <span className="ob-choice-hint">{option.hint}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </Field>
  );
}

/** Compact pill selection, for longer option sets. */
export function TagSelect({ label, optional, hint, note, options, values = [], onChange, max }) {
  const atLimit = max ? values.length >= max : false;

  const toggle = (key) => {
    if (values.includes(key)) onChange(values.filter((v) => v !== key));
    else if (!atLimit) onChange([...values, key]);
  };

  return (
    <Field label={label} optional={optional} hint={hint} note={note}>
      <div className="ob-tags">
        {options.map((option) => {
          const selected = values.includes(option.key);
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={selected}
              className="ob-tag"
              data-selected={selected}
              style={atLimit && !selected ? { opacity: .45 } : undefined}
              onClick={() => toggle(option.key)}
            >
              {selected && <Check size={13} weight="bold" />}
              {option.label}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

export function SelectionCounter({ count, min, max }) {
  const met = count >= min;
  let text;
  if (!met) {
    const remaining = min - count;
    text = `Choose ${remaining} more`;
  } else if (max) {
    text = `${count} selected — up to ${max}`;
  } else {
    text = `${count} selected`;
  }
  return <span className="ob-selected-count" data-met={met}>{text}</span>;
}

/**
 * Searchable skill picker grouped by category, with free text allowed.
 */
export function SkillPicker({ catalog, chosen = [], onAdd }) {
  const [query, setQuery] = useState('');
  const chosenNames = useMemo(
    () => new Set(chosen.map((s) => (s.skill_name || '').toLowerCase())),
    [chosen]
  );

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return catalog
      .map((group) => ({
        ...group,
        skills: group.skills.filter(
          (name) => !chosenNames.has(name.toLowerCase()) &&
                    (!needle || name.toLowerCase().includes(needle))
        ),
      }))
      .filter((group) => group.skills.length > 0);
  }, [catalog, query, chosenNames]);

  const trimmed = query.trim();
  const exactExists = catalog.some((g) =>
    g.skills.some((s) => s.toLowerCase() === trimmed.toLowerCase())
  );
  const canAddCustom = trimmed.length >= 2 && !exactExists && !chosenNames.has(trimmed.toLowerCase());

  const add = (name, category) => {
    onAdd({ skill_name: name, category: category || 'other', proficiency: 3 });
    setQuery('');
  };

  return (
    <div>
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search skills, or type your own"
      />

      {canAddCustom && (
        <button
          type="button"
          className="ob-add-btn"
          style={{ marginTop: 10 }}
          onClick={() => add(trimmed, 'other')}
        >
          Add “{trimmed}”
        </button>
      )}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {groups.map((group) => (
          <div key={group.category}>
            <div className="ob-group-label" style={{ marginBottom: 9 }}>{group.label}</div>
            <div className="ob-tags">
              {group.skills.map((name) => (
                <button
                  key={name}
                  type="button"
                  className="ob-tag"
                  onClick={() => add(name, group.category)}
                >
                  + {name}
                </button>
              ))}
            </div>
          </div>
        ))}
        {groups.length === 0 && !canAddCustom && (
          <div className="ob-empty">
            Nothing left matching “{query}”. Try a different word, or type your own above.
          </div>
        )}
      </div>
    </div>
  );
}

/** A chosen skill with its confidence level. */
export function SkillRow({ skill, levels, onChange, onRemove }) {
  return (
    <div className="ob-skill-row">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
        <span className="ob-skill-name">
          {skill.skill_name}
          <button
            type="button"
            className="ob-icon-btn"
            aria-label={`Remove ${skill.skill_name}`}
            onClick={onRemove}
          >
            <X size={14} weight="bold" />
          </button>
        </span>
        <label className="ob-learning-toggle">
          <input
            type="checkbox"
            checked={!!skill.is_currently_learning}
            onChange={(e) => onChange({ ...skill, is_currently_learning: e.target.checked })}
          />
          Currently learning this
        </label>
      </div>

      <div>
        <div className="ob-levels" role="radiogroup" aria-label={`Confidence with ${skill.skill_name}`}>
          {levels.map((level) => (
            <button
              key={level.value}
              type="button"
              role="radio"
              aria-checked={skill.proficiency === level.value}
              aria-label={level.label}
              title={`${level.label} — ${level.hint}`}
              className="ob-level-dot"
              data-on={skill.proficiency >= level.value}
              onClick={() => onChange({ ...skill, proficiency: level.value })}
            >
              {level.value}
            </button>
          ))}
        </div>
        <div className="ob-hint" style={{ marginTop: 5, textAlign: 'right' }}>
          {levels.find((l) => l.value === skill.proficiency)?.label}
        </div>
      </div>
    </div>
  );
}

/** Agreement scale. No dimension labels — this is not presented as a test. */
export function LikertItem({ statement, scale, value, onChange }) {
  return (
    <div className="ob-likert" data-answered={!!value}>
      <p className="ob-likert-text">{statement.text}</p>
      <div className="ob-likert-scale" role="radiogroup" aria-label={statement.text}>
        {scale.map((point) => (
          <button
            key={point.value}
            type="button"
            role="radio"
            aria-checked={value === point.value}
            aria-label={point.label}
            className="ob-likert-opt"
            data-on={value === point.value}
            onClick={() => onChange(point.value)}
          >
            <span className="ob-likert-bar" aria-hidden="true" />
            <span className="ob-likert-num" aria-hidden="true">{point.value}</span>
            <span className="ob-likert-caption">{point.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Drag-to-rank with arrow-button fallback, because drag alone is unusable on
 * touch and with a keyboard.
 */
export function RankList({ items, order, onChange }) {
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="ob-rank">
      {order.map((key, index) => {
        const item = items.find((i) => i.key === key);
        if (!item) return null;
        return (
          <div
            key={key}
            className="ob-rank-item"
            data-top={index === 0}
            data-dragging={dragging === index}
            data-over={over === index && dragging !== index}
            draggable
            onDragStart={() => setDragging(index)}
            onDragEnd={() => { setDragging(null); setOver(null); }}
            onDragOver={(e) => { e.preventDefault(); setOver(index); }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragging !== null && dragging !== index) move(dragging, index);
              setDragging(null);
              setOver(null);
            }}
          >
            <span className="ob-rank-num">{index + 1}</span>
            <DotsSixVertical size={17} className="ob-rank-grip" aria-hidden="true" />
            <div className="ob-rank-text">
              <div className="ob-rank-name">{item.name}</div>
              {item.tagline && <div className="ob-rank-tag">{item.tagline}</div>}
            </div>
            <div className="ob-rank-controls">
              <button
                type="button"
                className="ob-rank-btn"
                disabled={index === 0}
                aria-label={`Move ${item.name} up`}
                onClick={() => move(index, index - 1)}
              >
                <CaretUp size={13} weight="bold" />
              </button>
              <button
                type="button"
                className="ob-rank-btn"
                disabled={index === order.length - 1}
                aria-label={`Move ${item.name} down`}
                onClick={() => move(index, index + 1)}
              >
                <CaretDown size={13} weight="bold" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
